import {Box, Text} from '@mantine/core';
import React from 'react';

import {
  CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR,
  ConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {ConjugationSiteChips} from 'molbio/adcs/registration-table/AdcConjugationSitesDisplay';
import useAdcConjugationSitesCell from 'molbio/adcs/registration-table/useAdcConjugationSitesCell';
import getTestProps from 'test/lib/cypress/getTestProps';

export const SELECT_PROTEIN_FIRST_MESSAGE = 'Select a protein first';
export const SELECT_CONJUGATION_METHOD_FIRST_MESSAGE = 'Select a conjugation method first';

interface ConjugationSitesPlaceholderProps {
  sites: Array<ConjugationSite>;
  message: string;
}

/** Shows the selected sites, or a hint explaining why no sites are selected yet. */
function ConjugationSitesPlaceholder({sites, message}: ConjugationSitesPlaceholderProps): JSX.Element {
  if (sites.length > 0) {
    return <ConjugationSiteChips sites={sites} />;
  }
  return (
    <Text size="sm" color="dimmed">
      {message}
    </Text>
  );
}

interface AdcConjugationSitesCellProps {
  /** The cell's selected sites. */
  sites: Array<ConjugationSite>;
}

/**
 * Unlocked Conjugation sites cell in an ADC registration table. Shows the sites selected for the row's Protein
 * and Conjugation method (see useAdcConjugationSitesCell), or a hint until both are chosen. Must render inside
 * a table row (see RowModelProvider).
 */
export default function AdcConjugationSitesCell({sites}: AdcConjugationSitesCellProps): JSX.Element {
  const {antibodyId, conjugationMethod, candidates} = useAdcConjugationSitesCell(sites);

  let content: JSX.Element | null;
  if (antibodyId == null) {
    content = <ConjugationSitesPlaceholder sites={sites} message={SELECT_PROTEIN_FIRST_MESSAGE} />;
  } else if (conjugationMethod == null) {
    content = <ConjugationSitesPlaceholder sites={sites} message={SELECT_CONJUGATION_METHOD_FIRST_MESSAGE} />;
  } else if (candidates?.supported === false) {
    content = (
      <ConjugationSitesPlaceholder sites={sites} message={CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR} />
    );
  } else {
    content = <ConjugationSiteChips sites={sites} />;
  }
  return <Box {...getTestProps({component: 'AdcConjugationSitesCell', element: 'root'})}>{content}</Box>;
}
